import {InfoTriggerTooltip} from '#/atoms/info-trigger-tooltip';
import {LoadingSpinner} from '#/atoms/loading-spinner';
import AsyncButton, {AsyncButtonProps} from '#/components/async-button/component';
import {useLandRentalYield} from '#/hooks/query/use-land-rental-yield';
import {Land} from '@shareland/shareland-common';
import {FunctionComponent, HTMLProps, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

export const ActionMenuTransactionReviewButton: FunctionComponent<AsyncButtonProps> = props => {
  const {t} = useTranslation('translation');

  return (
    <AsyncButton type='submit' {...props}>
      {t('pages.actionMenu.ctaReviewTransaction')}
    </AsyncButton>
  );
};

type ActionMenuContentSectionInfoRowProps = {
  children?: {
    left: ReactNode | string;
    right: ReactNode | string;
    tooltip?: ReactNode;
  };
  title?: string;
  value?: string | number;
  leftClassName?: string;
  rightClassName?: string;
};
export const ActionMenuContentSectionInfoRow: FunctionComponent<ActionMenuContentSectionInfoRowProps> = ({
  title,
  value,
  children,
  leftClassName = '',
  rightClassName = 'text-white',
}) => (
  <div className='w-full flex justify-between items-center text-xs text-gray-300 h-4 overflow-visible'>
    <div className='flex gap-1 items-center'>
      <span className={leftClassName}>{title ?? children?.left}</span>
      {children?.tooltip && (
        <InfoTriggerTooltip tooltipId={(children.left?.toString() ?? title)!}>{children.tooltip}</InfoTriggerTooltip>
      )}
    </div>
    <span className={rightClassName}>{value ?? children?.right}</span>
  </div>
);

const ActionMenuContentSectionInfoRowRentalYieldRightContent: FunctionComponent<{land: Land}> = ({land}) => {
  const {data, isLoading, error} = useLandRentalYield(land.tokenSymbol);

  const {t} = useTranslation('translation');

  if (isLoading) {
    return <LoadingSpinner className={'w-4 h-4 text-deepblue fill-graybluedark'} />;
  }

  if (error) {
    <span className='text-downred italic'>{t('pages.actionMenu.components.contentSection.infoRow.errorLoading')}</span>;
  }

  if (data === undefined || data === null) {
    return <span>{t('pages.actionMenu.components.contentSection.infoRow.empty')}</span>;
  }

  return <span>{t('utils.percentage', {value: data})}</span>;
};

export const ActionMenuContentSectionInfoRowRentalYield: FunctionComponent<{land: Land}> = ({land}) => {
  const {t} = useTranslation('translation');

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t(
          'pages.actionMenu.information.sections.information.contentSection.information.infoRows.rentalYield.title',
        ),
        right: <ActionMenuContentSectionInfoRowRentalYieldRightContent land={land} />,
        tooltip: t(
          'pages.actionMenu.information.sections.information.contentSection.information.infoRows.rentalYield.tooltip',
        ),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

export const ActionMenuFormContainer: FunctionComponent<HTMLProps<HTMLFormElement>> = ({
  className,
  children,
  ...rest
}) => {
  return (
    <form className={`relative h-full flex flex-col items-stretch ${className}`} {...rest}>
      {children}
    </form>
  );
};
